import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type AccessPolicy, type AccountRequest, type AuditLog, type NotificationSettings, type PortalUser, type Report } from '../lib/api';
import { PolicyEditor } from '../components/PolicyEditor';
import { UserEditModal } from '../components/UserEditModal';
import { AccessRequestForm } from '../components/AccessRequestForm';

interface EditState {
  friendlyName: string;
  description: string;
  reportDate: string;
  year: number | '';
  docType: string;
  businessUnit: string;
}

type ReportSortKey = 'id' | 'docType' | 'businessUnit' | 'year' | 'friendlyName' | 'description' | 'reportDate' | 'uploadedBy';
type SortDir = 'asc' | 'desc';

function buildReportId(year: number, docType: string, businessUnit: string, existingReports: Report[]): string {
  const bu = businessUnit.replace(/\s+/g, '').replace(/\./g, '');
  const base = bu ? `${year}-${bu}-${docType}` : `${year}-${docType}`;
  const matches = existingReports.filter((r) => r.id === base || r.id.match(new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(-\\d+)?$`)));
  if (matches.length === 0) return base;
  return `${base}-${matches.length + 1}`;
}

export function AdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'requests' | 'reports' | 'users' | 'audit' | 'settings' | 'debug'>('requests');
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [usersTruncated, setUsersTruncated] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditNextKey, setAuditNextKey] = useState<string | null>(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    friendlyName: '',
    description: '',
    reportDate: '',
    year: '',
    docType: 'PCI',
    businessUnit: '',
  });
  const [reportSort, setReportSort] = useState<{ key: ReportSortKey; dir: SortDir }>({ key: 'friendlyName', dir: 'asc' });
  const [showUpload, setShowUpload] = useState(false);
  const [showAccessPreview, setShowAccessPreview] = useState(false);
  const [uploadState, setUploadState] = useState({
    reportId: '',
    friendlyName: '',
    description: '',
    reportDate: '',
    docType: 'PCI',
    businessUnit: '',
    year: new Date().getFullYear(),
    file: null as File | null,
  });
  const [uploadProgress, setUploadProgress] = useState<string>('');

  const [approvingRequest, setApprovingRequest] = useState<AccountRequest | null>(null);
  const [approvalPolicy, setApprovalPolicy] = useState<AccessPolicy | null>(null);

  const [editingPolicyFor, setEditingPolicyFor] = useState<PortalUser | null>(null);
  const [editingPolicyValue, setEditingPolicyValue] = useState<AccessPolicy | null>(null);

  const [editingUser, setEditingUser] = useState<PortalUser | null>(null);

  const [notifSettings, setNotifSettings] = useState<NotificationSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [whitelist, setWhitelist] = useState<import('../lib/api').WhitelistEntry[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainTypes, setNewDomainTypes] = useState<string[]>(['PCI']);

  const [debugLogs, setDebugLogs] = useState<{ timestamp: number; message: string; stream: string }[]>([]);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugMinutes, setDebugMinutes] = useState(60);
  const [debugFilter, setDebugFilter] = useState('');
  const [debugNextToken, setDebugNextToken] = useState<string | null>(null);
  const [debugLogGroup, setDebugLogGroup] = useState('');

  useEffect(() => {
    if (tab === 'requests') loadRequests();
    else if (tab === 'reports') loadReports();
    else if (tab === 'users') loadUsers();
    else if (tab === 'settings') loadSettings();
    else if (tab === 'debug') loadDebugLogs();
    else loadAuditLogs();
  }, [tab]);

  async function loadRequests() {
    setLoading(true);
    setError('');
    try {
      const data = await api.listPendingRequests();
      setRequests(data.requests);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }

  async function loadAuditLogs(append = false, lastKey?: string) {
    setLoading(true);
    setError('');
    try {
      const data = await api.getAuditLogs(lastKey);
      setAuditLogs((prev) => append ? [...prev, ...data.logs] : data.logs);
      setAuditNextKey(data.nextKey);
      setAuditHasMore(data.hasMore);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }

  async function loadSettings() {
    setSettingsLoading(true);
    setError('');
    try {
      const data = await api.getSettings();
      setNotifSettings(data.settings);
      setWhitelist(data.whitelist || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setSettingsLoading(false);
    }
  }

  async function toggleSetting(key: keyof NotificationSettings) {
    if (!notifSettings) return;
    const updated = { [key]: !notifSettings[key] };
    try {
      const data = await api.updateSettings(updated);
      setNotifSettings(data.settings);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    }
  }

  async function addWhitelistDomain(e: React.FormEvent) {
    e.preventDefault();
    const domain = newDomain.trim().toLowerCase();
    if (!domain || newDomainTypes.length === 0) return;
    setError('');
    try {
      const data = await api.addWhitelistDomain(domain, newDomainTypes);
      setWhitelist(data.whitelist);
      setNewDomain('');
      setNewDomainTypes(['PCI']);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add domain');
    }
  }

  async function toggleWhitelistEnabled(domain: string, enabled: boolean) {
    setError('');
    try {
      const data = await api.updateWhitelistDomain(domain, { enabled });
      setWhitelist(data.whitelist);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update domain');
    }
  }

  async function updateWhitelistTypes(domain: string, approvedTypes: string[]) {
    setError('');
    try {
      const data = await api.updateWhitelistDomain(domain, { approvedTypes });
      setWhitelist(data.whitelist);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update domain');
    }
  }

  async function removeWhitelistDomain(domain: string) {
    if (!confirm(`Remove ${domain} from the whitelist?`)) return;
    setError('');
    try {
      const data = await api.removeWhitelistDomain(domain);
      setWhitelist(data.whitelist);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain');
    }
  }

  async function loadDebugLogs(append = false, nextToken?: string) {
    setDebugLoading(true);
    setError('');
    try {
      const data = await api.getDebugLogs({
        minutes: debugMinutes,
        filter: debugFilter || undefined,
        nextToken,
      });
      const filtered = data.events.filter((evt) =>
        !(/^(START|END|REPORT|INIT_START) /.test(evt.message))
      );
      setDebugLogs((prev) => append ? [...prev, ...filtered] : filtered);
      setDebugNextToken(data.nextToken);
      setDebugLogGroup(data.logGroup);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load debug logs');
    } finally {
      setDebugLoading(false);
    }
  }

  async function loadReports() {
    setLoading(true);
    setError('');
    try {
      const data = await api.listReports();
      setReports(data.reports);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  async function loadUsers() {
    setLoading(true);
    setError('');
    try {
      const data = await api.listUsers();
      setUsers(data.users);
      setUsersTruncated(data.truncated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  function requestBadge(status: string): string {
    if (status === 'APPROVED') return 'ok';
    if (status === 'DENIED') return 'warn';
    return 'pending';
  }

  async function submitApproval() {
    if (!approvingRequest || !approvalPolicy) return;
    const req = approvingRequest;
    setApprovingRequest(null);
    setApprovalPolicy(null);
    await handleAction(req, 'APPROVED', approvalPolicy);
    if (tab === 'users') loadUsers();
  }

  async function submitPolicyEdit() {
    if (!editingPolicyFor || !editingPolicyValue) return;
    const target = editingPolicyFor;
    const policy = editingPolicyValue;
    setActionInProgress(`policy-${target.email}`);
    setError('');
    try {
      await api.setAccessPolicy(target.email, policy);
      setUsers((prev) =>
        prev.map((x) => (x.email === target.email ? { ...x, accessPolicy: policy } : x)),
      );
      setEditingPolicyFor(null);
      setEditingPolicyValue(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update access policy');
    } finally {
      setActionInProgress(null);
    }
  }

  function startEdit(report: Report) {
    setEditingId(report.id);
    setEditState({
      friendlyName: report.friendlyName || '',
      description: report.description || '',
      reportDate: report.reportDate || '',
      year: report.year || '',
      docType: report.docType || 'PCI',
      businessUnit: report.businessUnit || '',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditState({ friendlyName: '', description: '', reportDate: '', year: '', docType: 'PCI', businessUnit: '' });
  }

  async function saveEdit(reportId: string) {
    setActionInProgress(`edit-${reportId}`);
    setError('');
    try {
      await api.editReport(reportId, {
        friendlyName: editState.friendlyName,
        description: editState.description,
        reportDate: editState.reportDate,
        year: editState.year === '' ? undefined : Number(editState.year),
        docType: editState.docType,
        businessUnit: editState.businessUnit,
      });
      setReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? {
                ...r,
                friendlyName: editState.friendlyName,
                description: editState.description,
                reportDate: editState.reportDate,
                year: editState.year === '' ? undefined : Number(editState.year),
                docType: editState.docType,
                businessUnit: editState.businessUnit,
              }
            : r,
        ),
      );
      cancelEdit();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save report');
    } finally {
      setActionInProgress(null);
    }
  }

  function resetUpload() {
    setShowUpload(false);
    setUploadState({ reportId: '', friendlyName: '', description: '', reportDate: '', docType: 'PCI', businessUnit: '', year: new Date().getFullYear(), file: null });
    setUploadProgress('');
  }

  function toggleReportSort(key: ReportSortKey) {
    setReportSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }

  const sortedReports = useMemo(() => {
    const { key, dir } = reportSort;
    return [...reports].sort((a, b) => {
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      if (aVal < bVal) return dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reports, reportSort]);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadState.file) {
      setError(t('admin.reports.fileRequired'));
      return;
    }
    if (!uploadState.reportId.trim()) {
      setError(t('admin.reports.idRequired'));
      return;
    }

    setActionInProgress('upload');
    setError('');
    try {
      setUploadProgress(t('admin.reports.requestingUrl'));
      const { uploadUrl } = await api.uploadReport({
        reportId: uploadState.reportId.trim(),
        friendlyName: uploadState.friendlyName.trim() || undefined,
        description: uploadState.description.trim() || undefined,
        reportDate: uploadState.reportDate || undefined,
        docType: uploadState.docType,
        businessUnit: uploadState.businessUnit || undefined,
        year: uploadState.year,
      });

      setUploadProgress(t('admin.reports.uploading'));
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': uploadState.file!.type || 'application/octet-stream' },
        body: uploadState.file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed (${putRes.status})`);
      }

      setUploadProgress(t('admin.reports.refreshing'));
      await loadReports();
      resetUpload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleDelete(reportId: string) {
    if (!confirm(`Delete report "${reportId}"? This removes the PDF from storage.`)) return;
    setActionInProgress(`delete-${reportId}`);
    setError('');
    try {
      await api.deleteReport(reportId);
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete report');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleArchive(reportId: string, archived: boolean) {
    setActionInProgress(`archive-${reportId}`);
    setError('');
    try {
      await api.archiveReport(reportId, archived);
      setReports((prev) => prev.map((r) => r.id === reportId ? { ...r, archived } : r));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive report');
    } finally {
      setActionInProgress(null);
    }
  }

  async function handleAction(
    request: AccountRequest,
    action: 'APPROVED' | 'DENIED',
    accessPolicy?: AccessPolicy,
  ) {
    const key = `${request.email}-${action}`;
    setActionInProgress(key);
    try {
      await api.handleRequest({
        email: request.email,
        requestedAt: request.requestedAt,
        action,
        accessPolicy,
      });
      setRequests((prev) => prev.filter((r) => !(r.email === request.email && r.requestedAt === request.requestedAt)));
      if (tab === 'users') loadUsers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Failed to ${action.toLowerCase()} request`);
    } finally {
      setActionInProgress(null);
    }
  }

  return (
    <div className="admin-page">
      <h2>{t('admin.title')}</h2>

      <div className="tab-bar">
        <button
          className={tab === 'requests' ? 'tab active' : 'tab'}
          onClick={() => setTab('requests')}
        >
          {t('admin.tabs.requests')} ({requests.length})
        </button>
        <button
          className={tab === 'reports' ? 'tab active' : 'tab'}
          onClick={() => setTab('reports')}
        >
          {t('admin.tabs.reports')}
        </button>
        <button
          className={tab === 'users' ? 'tab active' : 'tab'}
          onClick={() => setTab('users')}
        >
          {t('admin.tabs.users')}
        </button>
        <button
          className={tab === 'audit' ? 'tab active' : 'tab'}
          onClick={() => setTab('audit')}
        >
          {t('admin.tabs.audit')}
        </button>
        <button
          className={tab === 'settings' ? 'tab active' : 'tab'}
          onClick={() => setTab('settings')}
        >
          {t('admin.tabs.settings', 'Settings')}
        </button>
        <button
          className={tab === 'debug' ? 'tab active' : 'tab'}
          onClick={() => setTab('debug')}
        >
          {t('admin.tabs.debug', 'Debug')}
        </button>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : tab === 'reports' ? (
        <div className="reports-admin-panel">
          <div className="reports-admin-toolbar">
            {!showUpload && (
              <button
                className="btn-primary"
                onClick={() => {
                  setShowUpload(true);
                  setError('');
                }}
              >
                {t('admin.reports.upload')}
              </button>
            )}
          </div>

          {showUpload && (
            <form className="upload-form" onSubmit={handleUpload}>
              <h3>{t('admin.reports.uploadTitle')}</h3>
              <p className="upload-hint">
                {t('admin.reports.uploadHint')}
              </p>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="upload-id">{t('admin.reports.reportId')} *</label>
                  <input
                    id="upload-id"
                    type="text"
                    value={uploadState.reportId}
                    onChange={(e) =>
                      setUploadState((s) => ({ ...s, reportId: e.target.value }))
                    }
                    placeholder="2026-AWS-AOC"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-doctype">{t('admin.reports.docType')}</label>
                  <select
                    id="upload-doctype"
                    value={uploadState.docType}
                    onChange={(e) => {
                      const docType = e.target.value;
                      setUploadState((s) => ({ ...s, docType, reportId: buildReportId(s.year, docType, s.businessUnit, reports) }));
                    }}
                    className="admin-input"
                  >
                    <option value="PCI">PCI</option>
                    <option value="SOC">SOC</option>
                    <option value="ISO">ISO</option>
                    <option value="Other">Other</option>
                    <option value="ROC">ROC</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="upload-bu">{t('admin.reports.businessUnit', 'Business Unit')}</label>
                  <select
                    id="upload-bu"
                    value={uploadState.businessUnit}
                    onChange={(e) => {
                      const businessUnit = e.target.value;
                      setUploadState((s) => ({ ...s, businessUnit, reportId: buildReportId(s.year, s.docType, businessUnit, reports) }));
                    }}
                    className="admin-input"
                  >
                    <option value="">— None —</option>
                    <option value="Amazon.com">Amazon.com</option>
                    <option value="APS(MENA)">APS(MENA)</option>
                    <option value="ASSPL">ASSPL</option>
                    <option value="APIPL">APIPL</option>
                    <option value="Just Walk Out">Just Walk Out</option>
                    <option value="Audible">Audible</option>
                    <option value="One Medical">One Medical</option>
                    <option value="Amazon Pay">Amazon Pay</option>
                    <option value="WWG/WFM">WWG/WFM</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="upload-year">{t('admin.reports.year', 'Year')}</label>
                  <select
                    id="upload-year"
                    value={uploadState.year}
                    onChange={(e) => {
                      const year = parseInt(e.target.value, 10);
                      setUploadState((s) => ({ ...s, year, reportId: buildReportId(year, s.docType, s.businessUnit, reports) }));
                    }}
                    className="admin-input"
                  >
                    {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="upload-friendly">{t('admin.reports.friendlyName')}</label>
                  <input
                    id="upload-friendly"
                    type="text"
                    value={uploadState.friendlyName}
                    onChange={(e) =>
                      setUploadState((s) => ({ ...s, friendlyName: e.target.value }))
                    }
                    placeholder="AWS PCI DSS v4.0 AOC"
                  />
                </div>
                <div className="form-group form-group-wide">
                  <label htmlFor="upload-desc">{t('admin.reports.description')}</label>
                  <textarea
                    id="upload-desc"
                    value={uploadState.description}
                    onChange={(e) =>
                      setUploadState((s) => ({ ...s, description: e.target.value }))
                    }
                    placeholder="Annual Attestation of Compliance covering Amazon retail and AWS payment scope."
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-date">{t('admin.reports.reportDate')}</label>
                  <input
                    id="upload-date"
                    type="date"
                    value={uploadState.reportDate}
                    onChange={(e) =>
                      setUploadState((s) => ({ ...s, reportDate: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="upload-file">{t('admin.reports.uploadTitle')} *</label>
                  <input
                    id="upload-file"
                    type="file"
                    onChange={(e) =>
                      setUploadState((s) => ({ ...s, file: e.target.files?.[0] ?? null }))
                    }
                    required
                  />
                </div>
              </div>
              {uploadProgress && (
                <div className="upload-progress" role="status">{uploadProgress}</div>
              )}
              <div className="upload-actions">
                <button
                  type="submit"
                  className="btn-approve"
                  disabled={actionInProgress === 'upload'}
                >
                  {actionInProgress === 'upload' ? t('admin.reports.uploading') : t('admin.reports.uploadBtn')}
                </button>
                <button
                  type="button"
                  className="btn-deny"
                  onClick={resetUpload}
                  disabled={actionInProgress === 'upload'}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {reports.length === 0 ? (
            <p className="empty-state">{t('admin.reports.empty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  {([
                    ['id', t('admin.reports.reportId')],
                    ['docType', t('admin.reports.type')],
                    ['businessUnit', t('admin.reports.businessUnit', 'Business Unit')],
                    ['year', t('admin.reports.year', 'Year')],
                    ['friendlyName', t('admin.reports.friendlyName')],
                    ['description', t('admin.reports.description')],
                    ['reportDate', t('admin.reports.reportDate')],
                    ['uploadedBy', t('admin.reports.uploadedBy')],
                  ] as [ReportSortKey, string][]).map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => toggleReportSort(key)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {label} {reportSort.key === key ? (reportSort.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  ))}
                  <th>{t('admin.reports.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedReports.map((r) => {
                  const isEditing = editingId === r.id;
                  const editKey = `edit-${r.id}`;
                  const deleteKey = `delete-${r.id}`;
                  return (
                    <tr key={r.id}>
                      <td>{r.id}{r.archived && <span className="badge badge-pending" style={{marginLeft: '8px'}}>{t('admin.reports.archived')}</span>}</td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editState.docType}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, docType: e.target.value }))
                            }
                            className="admin-input"
                          >
                            <option value="PCI">PCI</option>
                            <option value="SOC">SOC</option>
                            <option value="ISO">ISO</option>
                            <option value="Other">Other</option>
                            <option value="ROC">ROC</option>
                          </select>
                        ) : r.docType ? (
                          <span className={`badge badge-${r.docType === 'ROC' ? 'warn' : (r.docType || 'other').toLowerCase()}`}>
                            {r.docType}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editState.businessUnit}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, businessUnit: e.target.value }))
                            }
                            className="admin-input"
                          >
                            <option value="">— None —</option>
                            <option value="Amazon.com">Amazon.com</option>
                            <option value="APS(MENA)">APS(MENA)</option>
                            <option value="ASSPL">ASSPL</option>
                            <option value="APIPL">APIPL</option>
                            <option value="Just Walk Out">Just Walk Out</option>
                            <option value="Audible">Audible</option>
                            <option value="One Medical">One Medical</option>
                            <option value="Amazon Pay">Amazon Pay</option>
                            <option value="WWG/WFM">WWG/WFM</option>
                          </select>
                        ) : (
                          r.businessUnit || '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editState.year}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, year: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))
                            }
                            className="admin-input"
                          >
                            <option value="">—</option>
                            {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 3 + i).map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        ) : (
                          r.year || '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editState.friendlyName}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, friendlyName: e.target.value }))
                            }
                            placeholder={t('admin.reports.friendlyName')}
                            className="admin-input"
                          />
                        ) : (
                          r.friendlyName || '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editState.description}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, description: e.target.value }))
                            }
                            placeholder={t('admin.reports.description')}
                            className="admin-input"
                          />
                        ) : (
                          r.description || '—'
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editState.reportDate?.slice(0, 10) || ''}
                            onChange={(e) =>
                              setEditState((s) => ({ ...s, reportDate: e.target.value }))
                            }
                            className="admin-input"
                          />
                        ) : r.reportDate ? (
                          new Date(r.reportDate.slice(0, 10) + 'T00:00:00').toLocaleDateString()
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{r.uploadedBy || '—'}</td>
                      <td className="action-buttons">
                        {isEditing ? (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => saveEdit(r.id)}
                              disabled={actionInProgress === editKey}
                            >
                              {actionInProgress === editKey ? t('admin.reports.saving') : t('common.save')}
                            </button>
                            <button className="btn-deny" onClick={cancelEdit}>
                              {t('common.cancel')}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => startEdit(r)}
                              disabled={actionInProgress !== null}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              className="btn-secondary"
                              onClick={() => handleArchive(r.id, !r.archived)}
                              disabled={actionInProgress !== null}
                            >
                              {r.archived ? t('admin.reports.unarchive') : t('admin.reports.archive')}
                            </button>
                            <button
                              className="btn-deny"
                              onClick={() => handleDelete(r.id)}
                              disabled={actionInProgress !== null}
                            >
                              {actionInProgress === deleteKey ? t('admin.reports.deleting') : t('common.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === 'requests' ? (
        <div className="requests-panel">
          <div style={{ marginBottom: '1rem' }}>
            <button className="btn-primary" onClick={() => setShowAccessPreview(true)}>
              {t('admin.requests.previewForm', 'Preview Access Request Form')}
            </button>
          </div>
          {showAccessPreview && (
            <AccessRequestForm
              onClose={() => setShowAccessPreview(false)}
              onSubmitted={() => setShowAccessPreview(false)}
            />
          )}
          {requests.length === 0 ? (
            <p className="empty-state">{t('admin.requests.empty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('common.email')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.organization')}</th>
                  <th>{t('admin.requests.reason')}</th>
                  <th>{t('admin.requests.requestedDocuments', 'Requested Documents')}</th>
                  <th>{t('admin.requests.requested')}</th>
                  <th>{t('admin.reports.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={`${req.email}-${req.requestedAt}`}>
                    <td>
                      {req.email}
                      {req.reRequestPending && (
                        <span className="badge badge-pending" style={{ marginLeft: '8px' }}>
                          {req.requestType === 'replace' ? t('admin.requests.replace', 'Replace') : t('admin.requests.addTo', 'Add')}
                        </span>
                      )}
                    </td>
                    <td>{req.name}</td>
                    <td>{req.organization}</td>
                    <td>{req.reason || '—'}</td>
                    <td>
                      {req.requestedDocuments && req.requestedDocuments.length > 0 ? (
                        <ul className="requested-aoc-list">
                          {req.requestedDocuments.map((doc) => (
                            <li key={doc.reportId}>{doc.reportId} ({doc.accessMode})</li>
                          ))}
                        </ul>
                      ) : req.requestedAocReportIds && req.requestedAocReportIds.length > 0 ? (
                        <ul className="requested-aoc-list">
                          {req.requestedAocReportIds.map((id) => (
                            <li key={id}>{id}</li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted">{t('admin.requests.allUnspecified')}</span>
                      )}
                    </td>
                    <td>{new Date(req.requestedAt).toLocaleDateString()}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-approve"
                        onClick={() => setApprovingRequest(req)}
                        disabled={actionInProgress !== null}
                      >
                        {t('admin.requests.approve')}
                      </button>
                      <button
                        className="btn-deny"
                        onClick={() => handleAction(req, 'DENIED')}
                        disabled={actionInProgress !== null}
                      >
                        {t('admin.requests.deny')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === 'users' ? (
        <div className="users-panel">
          <div className="users-toolbar">
            <input
              type="search"
              placeholder={t('admin.users.filterPlaceholder')}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="admin-input users-search"
              aria-label={t('admin.users.filterPlaceholder')}
            />
            <span className="users-count">
              {t('admin.users.userCount', { count: users.length })}
              {usersTruncated && ` ${t('admin.users.truncated')}`}
            </span>
          </div>
          {users.length === 0 ? (
            <p className="empty-state">{t('admin.users.empty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('common.email')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('admin.users.org')}</th>
                  <th>{t('admin.users.authMethod')}</th>
                  <th>{t('admin.users.request')}</th>
                  <th>{t('admin.users.admin')}</th>
                  <th>{t('admin.reports.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter((u) => {
                    const q = userSearch.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      u.email.toLowerCase().includes(q) ||
                      (u.name || '').toLowerCase().includes(q) ||
                      (u.organization || '').toLowerCase().includes(q)
                    );
                  })
                  .map((u) => {
                    return (
                      <tr key={u.email}>
                        <td>{u.email}</td>
                        <td>{u.name || '—'}</td>
                        <td>{u.organization || '—'}</td>
                        <td>{u.authMethod || '—'}</td>
                        <td>
                          {u.requestStatus ? (
                            <span className={`badge badge-${requestBadge(u.requestStatus)}`}>
                              {u.requestStatus}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>{u.isAdmin ? t('admin.users.yes') : '—'}</td>
                        <td className="action-buttons">
                          <button
                            className="btn-secondary"
                            onClick={() => setEditingUser(u)}
                            disabled={actionInProgress !== null}
                          >
                            {t('common.edit')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === 'settings' ? (
        <div className="settings-panel">
          {settingsLoading ? (
            <p>{t('common.loading', 'Loading...')}</p>
          ) : notifSettings ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '480px' }}>
                <h3 style={{ margin: 0 }}>{t('admin.settings.emailTitle', 'Email Notifications')}</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={notifSettings.approveEmailIdprism}
                    onChange={() => toggleSetting('approveEmailIdprism')}
                  />
                  <span>{t('admin.settings.approveIdprism', 'Send approval emails to external (IdPrism) users')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={notifSettings.approveEmailMidway}
                    onChange={() => toggleSetting('approveEmailMidway')}
                  />
                  <span>{t('admin.settings.approveMidway', 'Send approval emails to internal (Midway) users')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={notifSettings.denyEmailIdprism}
                    onChange={() => toggleSetting('denyEmailIdprism')}
                  />
                  <span>{t('admin.settings.denyIdprism', 'Send denial emails to external (IdPrism) users')}</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={notifSettings.denyEmailMidway}
                    onChange={() => toggleSetting('denyEmailMidway')}
                  />
                  <span>{t('admin.settings.denyMidway', 'Send denial emails to internal (Midway) users')}</span>
                </label>
              </div>

              <div>
                <h3 style={{ margin: '0 0 0.5rem' }}>{t('admin.settings.whitelistTitle', 'Domain Auto-Approve Whitelist')}</h3>
                <p className="form-hint" style={{ margin: '0 0 1rem' }}>
                  {t('admin.settings.whitelistHint', 'Users from whitelisted domains get selected compliance document types auto-approved on request. Disable a rule to pause it without deleting.')}
                </p>

                <form onSubmit={addWhitelistDomain} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="e.g. jpmorgan.com"
                    className="admin-input"
                    style={{ flex: '1 1 180px' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {['PCI', 'SOC', 'ISO', 'ROC'].map((dt) => (
                      <label key={dt} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
                        <input
                          type="checkbox"
                          checked={newDomainTypes.includes(dt)}
                          onChange={(e) => {
                            setNewDomainTypes((prev) =>
                              e.target.checked ? [...prev, dt] : prev.filter((t) => t !== dt),
                            );
                          }}
                        />
                        {dt}
                      </label>
                    ))}
                  </div>
                  <button type="submit" className="btn-primary" disabled={!newDomain.trim() || newDomainTypes.length === 0}>
                    {t('admin.settings.addDomain', 'Add Domain')}
                  </button>
                </form>

                {whitelist.length === 0 ? (
                  <p className="empty-state">{t('admin.settings.whitelistEmpty', 'No domains whitelisted yet.')}</p>
                ) : (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>{t('admin.settings.enabled', 'Enabled')}</th>
                        <th>{t('admin.settings.domain', 'Domain')}</th>
                        <th>{t('admin.settings.approvedTypes', 'Auto-Approve Types')}</th>
                        <th>{t('admin.settings.addedBy', 'Added By')}</th>
                        <th>{t('admin.settings.addedAt', 'Added')}</th>
                        <th>{t('admin.reports.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {whitelist.map((entry) => (
                        <tr key={entry.domain} style={{ opacity: entry.enabled ? 1 : 0.5 }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={entry.enabled}
                              onChange={(e) => toggleWhitelistEnabled(entry.domain, e.target.checked)}
                              title={entry.enabled ? 'Disable rule' : 'Enable rule'}
                            />
                          </td>
                          <td><code>{entry.domain}</code></td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {['PCI', 'SOC', 'ISO', 'ROC'].map((dt) => (
                                <label key={dt} style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.8rem' }}>
                                  <input
                                    type="checkbox"
                                    checked={entry.approvedTypes.includes(dt)}
                                    onChange={(e) => {
                                      const updated = e.target.checked
                                        ? [...entry.approvedTypes, dt]
                                        : entry.approvedTypes.filter((t) => t !== dt);
                                      if (updated.length > 0) updateWhitelistTypes(entry.domain, updated);
                                    }}
                                  />
                                  {dt}
                                </label>
                              ))}
                            </div>
                          </td>
                          <td>{entry.addedBy}</td>
                          <td>{new Date(entry.addedAt).toLocaleDateString()}</td>
                          <td>
                            <button
                              className="btn-deny"
                              onClick={() => removeWhitelistDomain(entry.domain)}
                            >
                              {t('common.delete', 'Remove')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <p className="empty-state">{t('admin.settings.loadError', 'Could not load settings.')}</p>
          )}
        </div>
      ) : tab === 'debug' ? (
        <div className="debug-panel">
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={debugMinutes}
              onChange={(e) => setDebugMinutes(parseInt(e.target.value, 10))}
              className="admin-input"
            >
              <option value={15}>Last 15 min</option>
              <option value={60}>Last 1 hour</option>
              <option value={360}>Last 6 hours</option>
              <option value={1440}>Last 24 hours</option>
            </select>
            <input
              type="text"
              value={debugFilter}
              onChange={(e) => setDebugFilter(e.target.value)}
              placeholder='Filter (e.g. "ERROR", email, "signup")'
              className="admin-input"
              style={{ flex: '1 1 200px' }}
              onKeyDown={(e) => { if (e.key === 'Enter') loadDebugLogs(); }}
            />
            <button
              className="btn-primary"
              onClick={() => loadDebugLogs()}
              disabled={debugLoading}
            >
              {debugLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
          {debugLogGroup && (
            <p className="form-hint" style={{ margin: '0 0 0.5rem' }}>
              Log group: <code>{debugLogGroup}</code>
            </p>
          )}
          {debugNextToken && (
            <div style={{ marginBottom: '0.5rem' }}>
              <button
                className="btn-secondary"
                onClick={() => loadDebugLogs(true, debugNextToken)}
                disabled={debugLoading}
              >
                {debugLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          {debugLogs.length === 0 && !debugLoading ? (
            <p className="empty-state">No log events found for the selected time range and filter.</p>
          ) : (
            <div className="debug-log-list" style={{ maxHeight: '600px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
              <table className="admin-table" style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '160px' }}>Time</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {[...debugLogs].reverse().map((evt, i) => (
                    <tr key={`${evt.timestamp}-${i}`}>
                      <td className="mono" style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {new Date(evt.timestamp).toLocaleString()}
                      </td>
                      <td>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {evt.message}
                        </pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="audit-panel">
          {auditLogs.length === 0 ? (
            <p className="empty-state">{t('admin.audit.empty')}</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('admin.audit.timestamp')}</th>
                  <th>{t('admin.audit.user')}</th>
                  <th>{t('admin.audit.action')}</th>
                  <th>{t('admin.audit.outcome')}</th>
                  <th>{t('admin.audit.target')}</th>
                  <th>{t('admin.audit.sourceIp')}</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={`${log.pk}-${log.sk}`}>
                    <td>{new Date(log.timestamp).toLocaleString()}</td>
                    <td title={log.userAlias || ''}>{log.userId || log.user || '—'}</td>
                    <td>{log.eventType || log.action || '—'}</td>
                    <td>
                      <span className={`badge badge-${log.outcome === 'failure' ? 'warn' : 'ok'}`}>
                        {log.outcome || '—'}
                      </span>
                    </td>
                    <td>{log.affectedResource || log.reportId || log.targetEmail || log.targetAlias || '—'}</td>
                    <td className="mono">{log.sourceIp || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {auditHasMore && (
            <div className="pagination-controls">
              <button
                className="btn-secondary"
                onClick={() => loadAuditLogs(true, auditNextKey || undefined)}
                disabled={loading}
              >
                {loading ? t('admin.audit.loadingMore') : t('admin.audit.loadMore')}
              </button>
            </div>
          )}
        </div>
      )}

      {approvingRequest && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="approve-modal-title">
          <div className="modal-card modal-card-wide">
            <h2 id="approve-modal-title">{t('admin.approveModal.title', { email: approvingRequest.email })}</h2>
            <p className="modal-hint">{t('admin.approveModal.hint')}</p>
            {approvingRequest.reRequestPending && (
              <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
                <strong>
                  {approvingRequest.requestType === 'replace'
                    ? t('admin.approveModal.replaceRequest', 'User wants to replace their existing access')
                    : t('admin.approveModal.addRequest', 'User wants to add to their existing access')}
                </strong>
                {approvingRequest.existingPolicy && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                    <span>{t('admin.approveModal.currentAccess', 'Current access:')}{' '}</span>
                    {approvingRequest.existingPolicy.accessMode === 'view' ? t('policyEditor.viewOnly', 'View Only') : t('policyEditor.viewAndDownload', 'View & Download')}
                    {approvingRequest.existingPolicy.approvedTypes.length > 0 && (
                      <span> · {t('admin.approveModal.types', 'Types:')} {approvingRequest.existingPolicy.approvedTypes.join(', ')}</span>
                    )}
                    {approvingRequest.existingPolicy.approvedReportIds.length > 0 && (
                      <span> · {approvingRequest.existingPolicy.approvedReportIds.length} {t('admin.approveModal.specificDocs', 'specific doc(s)')}</span>
                    )}
                    {approvingRequest.existingPolicy.rocReportIds.length > 0 && (
                      <span> · {approvingRequest.existingPolicy.rocReportIds.length} ROC(s)</span>
                    )}
                    {approvingRequest.existingPolicy.expiresAt && (
                      <span> · {t('admin.approveModal.expires', 'Expires:')} {approvingRequest.existingPolicy.expiresAt}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {approvingRequest.requestedAocReportIds && approvingRequest.requestedAocReportIds.length > 0 && (
              <p className="modal-hint">
                {t('admin.approveModal.requestedHint')}{' '}
                {approvingRequest.requestedAocReportIds.map((id, i) => (
                  <span key={id}>
                    <code>{id}</code>
                    {i < (approvingRequest.requestedAocReportIds!.length - 1) && ', '}
                  </span>
                ))}
                . {t('admin.approveModal.prefilledBelow')}
              </p>
            )}
            <PolicyEditor
              initial={approvingRequest.reRequestPending && approvingRequest.requestType !== 'replace' ? approvingRequest.existingPolicy : undefined}
              onChange={setApprovalPolicy}
              showRoc
            />
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setApprovingRequest(null);
                  setApprovalPolicy(null);
                }}
                disabled={actionInProgress !== null}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-approve"
                onClick={submitApproval}
                disabled={actionInProgress !== null || !approvalPolicy}
              >
                {t('admin.approveModal.approveButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPolicyFor && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="edit-policy-title">
          <div className="modal-card modal-card-wide">
            <h2 id="edit-policy-title">{t('admin.editPolicyModal.title', { email: editingPolicyFor.email })}</h2>
            <PolicyEditor
              initial={editingPolicyFor.accessPolicy}
              onChange={setEditingPolicyValue}
              showRoc
            />
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditingPolicyFor(null);
                  setEditingPolicyValue(null);
                }}
                disabled={actionInProgress !== null}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn-approve"
                onClick={submitPolicyEdit}
                disabled={actionInProgress !== null || !editingPolicyValue}
              >
                {t('admin.savePolicy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onMutate={() => loadUsers()}
        />
      )}
    </div>
  );
}
